import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme } from '@cbridge-utils/mui-theme';
import { useTranslation } from 'react-i18next';

const PageContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  minHeight: '100vh',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  backgroundColor: theme.palette.background.default,
}));

const ContentCard = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
  maxWidth: 817,
  alignItems: 'center',
  padding: '100px',
  borderRadius: Number(theme.shape.borderRadius) * 4,
}));

const IconWrapper = styled(Box)({
  width: 230,
  height: 230,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto',
  marginRight: 'auto',
});

const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontFamily: 'monospace',
  backgroundColor: theme.palette.action.hover,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  maxWidth: '100%',
  overflow: 'auto',
}));

interface IErrorFallbackProps {
  error?: Error;
  onRetry?: () => void;
}

export const ErrorFallback = ({ error, onRetry }: IErrorFallbackProps) => {
  const { t } = useTranslation();

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <ThemeProvider theme={createTheme()}>
      <CssBaseline />
      <PageContainer>
        <ContentCard>
          <IconWrapper>
            <Box
              component={'img'}
              src={'/icons/icon_error.png'}
              alt={''}
              sx={{
                width: 230,
                height: 230,
              }} />
          </IconWrapper>

          <Typography variant={'h6'} sx={{ color: 'text.primary' }}>
            {t('text:common.error.title')}
          </Typography>

          <Typography
            variant={'body1'}
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
            }}>
            {t('text:common.error.description')}
          </Typography>

          {error?.message && (
            <ErrorMessage variant={'body2'}>
              {error.message}
            </ErrorMessage>
          )}

          <Button variant={'contained'} onClick={handleRetry}>
            {t('text:common.error.reloadPage')}
          </Button>
        </ContentCard>
      </PageContainer>
    </ThemeProvider>
  );
};
